// ContextBolt's own MCP servers, ContextBolt SEO and ContextBolt Bookmarks. Both sign people in with their account
// email and a code (OAuth), and both mark every tool "not read-only" when an app signs in that way (OpenAI's review
// counts a usage log as a write), so the board cannot go by their hints. It goes by this list instead: the tools
// that only read and cost nothing. SEO's lookups spend the user's credits, so only the board's own chat runs them,
// when the user asks it to, with seo_lookup (David, 14 Oct: free reads only; 17 Oct: the chat runs fresh research);
// a source, a brief, a job or an outside agent never does. get_board_link is left out because the link it returns
// is a secret. The lookups' prices are SEO's tool_costs rows, read from its database on 17 Oct 2026.
// Checked against contextbolt-seo origin/main 178efc2 (src/routes/mcp.ts, src/lib/research-tools.ts) and
// contextbolt-worker f7eae88 (src/routes/mcp.ts) on 14 Oct 2026.

export interface OurServer {
  id: "contextbolt-seo" | "contextbolt-bookmarks";
  name: string;
  /** the MCP address people connect */
  url: string;
  /** the product's page, with the board's campaign tags so the site counts the visits */
  page: string;
  /** what an account needs before its sign-in sends a code */
  needs: string;
  /** the tools the board may run: they only read, and spend nothing */
  reads: string[];
  /** what only the board's own chat runs, when the user asks: each spends this many of the user's credits and changes nothing else */
  lookups?: Record<string, number>;
  /** said to agents beside the list, about everything else the server has */
  rest: string;
}

const UTM = "?utm_source=context-board&utm_medium=apps";

export const OURS: OurServer[] = [
  {
    id: "contextbolt-seo",
    name: "ContextBolt SEO",
    url: "https://seo.contextbolt.app/mcp",
    page: `https://contextbolt.com/seo/${UTM}`,
    needs: "Needs a ContextBolt SEO plan",
    reads: [
      "check_credits",
      "list_recent_findings",
      "get_finding",
      "list_tracked",
      "list_research",
      "get_research",
      "research_history",
      "list_search_console_sites",
      "search_console_performance",
      "search_console_quick_wins",
      "inspect_url",
    ],
    // site_audit and ai_visibility_report run for minutes and spend 25 to 50 credits or more: the SEO Dashboard's own
    lookups: {
      keyword_research: 1,
      keyword_difficulty: 1,
      serp_overview: 1,
      domain_overview: 1,
      ranked_keywords: 1,
      competitor_keywords: 1,
      ai_keyword_volume: 1,
      page_audit: 1,
      ai_answer: 2,
      backlink_overview: 3,
      referring_domains: 3,
      backlink_gap: 3,
      ai_visibility: 8,
      ai_share_of_voice: 8,
    },
    rest: "Its other tools are lookups that spend the user's ContextBolt SEO credits, or change their account, so the board never runs them: for a new lookup, ask the user to run it in their own AI app, then read it here with list_research or list_recent_findings.",
  },
  {
    id: "contextbolt-bookmarks",
    name: "ContextBolt Bookmarks",
    url: "https://api.contextbolt.app/mcp",
    page: `https://contextbolt.com/bookmarks/${UTM}`,
    needs: "Needs ContextBolt Bookmarks Pro",
    reads: ["search_bookmarks", "list_clusters", "get_cluster_bookmarks", "get_recent_bookmarks", "export_bookmarks"],
    rest: "Its save_bookmark changes the user's library, so the board never runs it.",
  },
];

/**
 * An address on one of our hosts, at any path and over http or https. Counting an address as ours only ever narrows
 * what runs there to the list above, so the rule is as wide as the host. That takes in each product's personal link,
 * which its welcome email sends (SEO: https://seo.contextbolt.app/mcp/<token>, contextbolt-seo src/routes/webhooks.ts:400)
 * and where SEO lists its paid lookups as read-only (src/routes/mcp.ts:1156 keeps the old hints off OAuth), and any
 * spelling of a path or scheme the host still answers. A look-alike host is never ours.
 */
function onHost(base: string, url: string): boolean {
  try {
    const b = new URL(base);
    const u = new URL(url);
    return u.host === b.host && (u.protocol === "https:" || u.protocol === "http:");
  } catch {
    return false;
  }
}

// more addresses that count as ours, for a dev board testing against copies run locally: set once by the worker,
// from DEV_OURS, only when DEV_MODE is on, so every caller of ourServer agrees without passing it along
let devMap: string | null = null;

export function setDevOurs(map: string | null): void {
  devMap = map;
}

/** The dev mapping in use, for the worker's calls that pass it along. */
export function devOursNow(): string | null {
  return devMap;
}

/**
 * Ours, by its address. `dev` maps more addresses to ours ("contextbolt-seo=http://127.0.0.1:8787/mcp,..."), the
 * worker's dev mapping when it is left out.
 */
export function ourServer(url: string, dev: string | null = devMap): OurServer | null {
  const found = OURS.find((o) => onHost(o.url, url));
  if (found || !dev) return found ?? null;
  for (const pair of dev.split(",")) {
    const [id, at] = pair.split("=").map((s) => s.trim());
    if (at && onHost(at, url)) return OURS.find((o) => o.id === id) ?? null;
  }
  return null;
}

/** Whether the board may run a tool: on ours, by the list above; elsewhere, what the server's hints said. */
export function ourReads<T extends { name: string; reads: boolean }>(url: string, tools: T[], dev: string | null = devMap): T[] {
  const ours = ourServer(url, dev);
  return ours ? tools.map((t) => ({ ...t, reads: ours.reads.includes(t.name) })) : tools;
}

/** A lookup only the board's own chat runs, with what it costs, or null when the tool is none. */
export function ourLookup(url: string, tool: string, dev: string | null = devMap): { server: OurServer; credits: number } | null {
  const ours = ourServer(url, dev);
  const credits = ours?.lookups && Object.hasOwn(ours.lookups, tool) ? ours.lookups[tool] : undefined;
  return ours && credits ? { server: ours, credits } : null;
}

/** Why the board will not run a tool on one of ours, or null when it may. */
export function ourRefusal(url: string, tool: string, dev: string | null = devMap): string | null {
  const ours = ourServer(url, dev);
  if (!ours || ours.reads.includes(tool)) return null;
  return ours.id === "contextbolt-seo"
    ? ours.lookups && Object.hasOwn(ours.lookups, tool)
      ? `"${tool}" spends the user's ${ours.name} credits, so it runs only with seo_lookup, in the board's own chat, when the user asks for it. Anywhere else, run it in your own AI app, then read it here with list_research.`
      : `The board only runs ${ours.name}'s free reads, and "${tool}" changes the account. Run it in your own AI app, then read it here with list_research.`
    : `The board only runs ${possessive(ours.name)} reads, and "${tool}" changes the account.`;
}

/** "ContextBolt SEO's", "ContextBolt Bookmarks'". */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}
